import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, DatePicker, Empty, Input, Popover, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InfoCircleOutlined, LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { fct } from '../../fct/tokens/tokens';
import BarraLista, { BotonActualizar, ESTILO_BUSCADOR } from './BarraLista';
import {
  CampanasSemana, CandidatoSemana, TipoCompra, getCampanaSemana, getCampanasSemana,
} from '../../api/cxPanelApi';
import CampanasBrevo from './CampanasBrevo';
import {
  FILTRO_TODOS, FiltroTipo, TIPOS_COMPRA, TIPO_COLOR, TIPO_COLOR_HEX, TIPO_DESCRIPCION, aIsoDia,
  coincideBusqueda, coincideTipo, compararCandidatos, contarTipos, etiquetaSemana, juevesDeEnvio, lunesDe,
  lunesSemanaAnterior, mensajeError, rangoSemana,
} from './panelCx';

const { Text } = Typography;

/** Tag de tipo con su color y una línea de explicación en el tooltip (o la que se le pase). */
export const TipoTag: React.FC<{ tipo: string; titulo?: React.ReactNode }> = ({ tipo, titulo }) => (
  <Tooltip title={titulo ?? TIPO_DESCRIPCION[tipo]}>
    <Tag color={TIPO_COLOR[tipo] ?? 'default'} style={{ fontWeight: 600 }}>{tipo}</Tag>
  </Tooltip>
);

/**
 * Filtro de tipo con su contador, en desplegable para que la barra quepa en una fila: el contador
 * va en cada opción y en la elegida. Solo pinta los tipos que se le pasan. Las descripciones y los
 * colores de los contadores son, por defecto, los de las encuestas; la bandeja pasa los suyos.
 */
export const TipoFiltro: React.FC<{
  tipos: string[];
  value: FiltroTipo;
  onChange: (v: FiltroTipo) => void;
  conteos: Record<string, number>;
  total: number;
  /** Tooltip de cada tipo (por defecto, el de las encuestas). */
  descripciones?: Record<string, string>;
  /** Color del contador de cada tipo (por defecto, el de las encuestas; si falta, tinta secundaria). */
  colores?: Record<string, string>;
  /** Nombre visible de cada tipo (por defecto, el propio código: ROC, OC, SI…). */
  nombres?: Record<string, string>;
  /** Ancho del desplegable (140 px con códigos cortos). */
  ancho?: number;
}> = ({
  tipos, value, onChange, conteos, total, descripciones = TIPO_DESCRIPCION, colores = TIPO_COLOR_HEX, nombres, ancho = 140,
}) => {
  const opcion = (label: string, v: FiltroTipo, n: number, color: string) => ({
    value: v,
    title: v === FILTRO_TODOS ? 'Todos los tipos' : descripciones[v],
    label: (
      <Space size={8} align="center">
        <span>{label}</span>
        <Badge count={n} showZero overflowCount={9999} style={{ backgroundColor: color }} />
      </Space>
    ),
  });
  return (
    <Select<FiltroTipo>
      value={value}
      onChange={onChange}
      aria-label="Filtrar por tipo"
      popupMatchSelectWidth={false}
      // Todas las opciones a la vista, sin lista virtual (la rueda del ratón no la movía bien).
      listHeight={480}
      virtual={false}
      style={{ width: ancho }}
      options={[
        opcion('Todos', FILTRO_TODOS, total, fct.marca['verde-base']),
        ...tipos.map((t) => opcion(nombres?.[t] ?? t, t as FiltroTipo, conteos[t] ?? 0, colores[t] ?? fct.tinta['500'])),
      ]}
    />
  );
};

/** Regla del envío automático, en pasos, para consultarla sin salir de la pantalla. */
const ReglasEnvio: React.FC = () => {
  const paso = (n: number, pregunta: string, tipo: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
      <Badge count={n} style={{ backgroundColor: fct.tinta['500'] }} />
      <span style={{ flex: 1 }}>{pregunta}</span>
      <span>→</span>
      <TipoTag tipo={tipo} />
    </div>
  );
  return (
    <div style={{ maxWidth: 360 }}>
      <Text type="secondary">Cada jueves, con la semana anterior (lunes a domingo).</Text>
      <div style={{ margin: '10px 0 6px' }}><Text strong>Una encuesta por cliente y semana:</Text></div>
      {paso(1, '¿Tiene ROC esa semana?', 'ROC')}
      {paso(2, 'Si no, ¿tiene OC?', 'OC')}
      {paso(3, 'Si no, ¿tiene SI?', 'SI')}
      <div style={{ marginTop: 10 }}>
        <Text type="secondary">Si otra semana vuelve a tener ROC, OC o SI, recibe otra encuesta.</Text>
      </div>
    </div>
  );
};

/** Buscador de cliente común a las dos vistas. */
export const BuscadorCliente: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = (
  { value, onChange, placeholder },
) => (
  <Input
    allowClear
    prefix={<SearchOutlined />}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={ESTILO_BUSCADOR}
  />
);

interface Props {
  /** Título y selector de vista: van al principio de la barra (una sola fila con los filtros). */
  cabecera: React.ReactNode;
  /** Texto del buscador (común a las dos vistas): nombre, código CL, email, CCL u operación. */
  busqueda: string;
  onBusqueda: (v: string) => void;
}

/**
 * «Campaña de la semana»: vista previa de lo que el flujo n8n del jueves mandaría para la semana
 * elegida (lunes a domingo). Solo lectura: no reserva, no envía y no registra.
 */
const CampanaSemanaTab: React.FC<Props> = ({ cabecera, busqueda, onBusqueda }) => {
  const [semana, setSemana] = useState<Dayjs>(() => lunesSemanaAnterior());
  const [tipo, setTipo] = useState<FiltroTipo>(FILTRO_TODOS);
  const [candidatos, setCandidatos] = useState<CandidatoSemana[]>([]);
  const [campanas, setCampanas] = useState<CampanasSemana | null>(null);
  /** Brevo tarda algo más que Gescom: las tarjetas llevan su propio indicador de carga. */
  const [cargandoCampanas, setCargandoCampanas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Descarta respuestas de peticiones viejas si el usuario cambia de semana rápido. */
  const peticion = useRef(0);

  const lunes = useMemo(() => lunesDe(semana), [semana]);

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setLoading(true);
    setError(null);
    // Las campañas de Brevo van aparte: si Brevo falla, la tabla de Gescom se ve igual.
    setCargandoCampanas(true);
    getCampanasSemana(aIsoDia(lunes))
      .then((c) => { if (id === peticion.current) setCampanas(c); })
      .catch(() => { if (id === peticion.current) setCampanas(null); })
      .finally(() => { if (id === peticion.current) setCargandoCampanas(false); });
    try {
      const { candidatos: datos } = await getCampanaSemana(aIsoDia(lunes));
      if (id !== peticion.current) return;
      setCandidatos([...datos].sort(compararCandidatos));
    } catch (err) {
      if (id !== peticion.current) return;
      setCandidatos([]);
      setError(mensajeError(err, 'la campaña de la semana'));
    } finally {
      if (id === peticion.current) setLoading(false);
    }
  }, [lunes]);

  useEffect(() => { cargar(); }, [cargar]);

  // Contadores sobre lo que pasa el buscador (así «Todos 3 · ROC 1…» responde a la búsqueda);
  // la tabla, además, por el tipo elegido.
  const buscados = useMemo(
    () => candidatos.filter((c) => coincideBusqueda(busqueda, c.nombre, c.codigoCliente, c.email, c.ccl, c.nombreOperacion)),
    [candidatos, busqueda],
  );
  const conteos = useMemo(() => contarTipos(buscados), [buscados]);
  const calculados = useMemo(() => contarTipos(candidatos), [candidatos]);
  const visibles = useMemo(() => buscados.filter((c) => coincideTipo(tipo, c.tipo)), [buscados, tipo]);
  const clientes = useMemo(() => new Set(visibles.map((c) => c.idCliente)).size, [visibles]);
  const filtrando = visibles.length !== candidatos.length;
  const jueves = juevesDeEnvio(lunes);

  const columnas: ColumnsType<CandidatoSemana> = [
    {
      title: 'Fecha',
      dataIndex: 'fechaRegistro',
      width: 105,
      render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
      sorter: (a, b) => (a.fechaRegistro ?? '').localeCompare(b.fechaRegistro ?? ''),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 80,
      render: (v: TipoCompra) => <TipoTag tipo={v} />,
    },
    {
      title: 'CL',
      dataIndex: 'codigoCliente',
      width: 110,
      render: (v?: string | null, fila?: CandidatoSemana) => v || <Text type="secondary">#{fila?.idCliente}</Text>,
    },
    {
      title: 'CCL',
      dataIndex: 'ccl',
      width: 150,
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Operación',
      dataIndex: 'nombreOperacion',
      width: 200,
      ellipsis: true,
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Cliente',
      dataIndex: 'nombre',
      ellipsis: true,
      render: (v?: string | null) => v || <Text type="secondary">Sin nombre</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 280,
      ellipsis: true,
      render: (v?: string | null) => (v
        ? v
        : <Tag color="warning">Sin email en Gescom</Tag>),
    },
  ];

  return (
    <>
      <BarraLista
        cabecera={cabecera}
        acciones={
          /* Cuándo sale y con qué reglas, en un solo botón (clic: las reglas de envío). */
          <Popover title="Reglas de envío" content={<ReglasEnvio />} trigger="click" placement="bottomRight">
            <Tooltip title="Ver las reglas de envío">
              <Button
                icon={<InfoCircleOutlined />}
                style={{
                  background: fct.estado.aviso.relleno,
                  borderColor: `${fct.estado.aviso.linea}55`,
                  color: fct.estado.aviso.texto,
                }}
              >
                Sale el jueves {jueves.format('DD/MM')}
              </Button>
            </Tooltip>
          </Popover>
        }
        filtros={
          <>
            <TipoFiltro tipos={TIPOS_COMPRA} value={tipo} onChange={setTipo} conteos={conteos} total={buscados.length} />
            {/* Navegación por semanas: flechas para ir a la anterior / siguiente, el calendario para saltar
                lejos y un atajo a la semana que procesa la campaña del jueves. */}
            <Space size={4}>
              <Tooltip title="Semana anterior">
                <Button icon={<LeftOutlined />} aria-label="Semana anterior" onClick={() => setSemana(lunes.subtract(7, 'day'))} />
              </Tooltip>
              <DatePicker
                picker="week"
                allowClear={false}
                value={semana}
                onChange={(v) => v && setSemana(v)}
                format={(v) => rangoSemana(v)}
                style={{ width: 175 }}
                // Semanas que aún no han empezado no tienen nada que previsualizar.
                disabledDate={(d) => lunesDe(d).isAfter(dayjs(), 'day')}
              />
              <Tooltip title="Semana siguiente">
                <Button
                  icon={<RightOutlined />}
                  aria-label="Semana siguiente"
                  disabled={lunes.add(7, 'day').isAfter(dayjs(), 'day')}
                  onClick={() => setSemana(lunes.add(7, 'day'))}
                />
              </Tooltip>
              <Button disabled={lunes.isSame(lunesSemanaAnterior(), 'day')} onClick={() => setSemana(lunesSemanaAnterior())}>
                Última semana
              </Button>
            </Space>
          </>
        }
      >
        <BuscadorCliente value={busqueda} onChange={onBusqueda} placeholder="Buscar: nombre, CL, email, CCL u operación" />
        <BotonActualizar onClick={cargar} loading={loading} />
      </BarraLista>

      <CampanasBrevo datos={campanas} loading={cargandoCampanas} calculados={calculados} />

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error}
          action={<Button size="small" onClick={cargar}>Reintentar</Button>}
        />
      )}

      <Table<CandidatoSemana>
        rowKey={(f) => `${f.tipo}-${f.idCliente}`}
        columns={columnas}
        dataSource={visibles}
        loading={loading}
        size="small"
// Con la tabla vacía, sin barra de desplazamiento horizontal (se veía una barra suelta bajo «No hay…»).
        scroll={visibles.length ? { x: 1000 } : undefined}
        locale={{
          emptyText: error
            ? <Empty description="No se ha podido cargar la campaña" />
            : filtrando
              ? <Empty description="Ningún envío de esta semana coincide con la búsqueda o el tipo" />
              : <Empty description={`Nadie recibiría encuesta por la ${etiquetaSemana(lunes).toLowerCase()}`} />,
        }}
        pagination={{
          defaultPageSize: 50,
          pageSizeOptions: [25, 50, 100, 200],
          showSizeChanger: true,
          showTotal: (t) => `${filtrando ? `${t} de ${candidatos.length}` : t} envíos · ${clientes} clientes`,
        }}
      />
    </>
  );
};

export default CampanaSemanaTab;
